/**
 * Reading vector tiles out of a PMTiles archive.
 *
 * Buildings and land use used to be shipped whole — 2.8MB and 5.8MB gzipped,
 * about nine tenths of the cold load — because the client needed all of one
 * downtown and one downtown fits in memory. Neither is true at a hundred-mile
 * radius: the payload grows with the territory rather than with what is on
 * screen, and the browser would be fetching hundreds of megabytes to draw one
 * block.
 *
 * A single archive, byte-range requested per tile, is how the basemap has
 * always worked. This gives the deck.gl layers the same thing.
 *
 * deck.gl's own MVTLayer wants a `{z}/{x}/{y}` URL template and would need a
 * tile server in front of the archive. TileLayer with a custom `getTileData`
 * skips that: pmtiles resolves the byte range, MVTLoader parses what comes
 * back, and nothing has to be running server-side but nginx.
 */

import { TileLayer } from "@deck.gl/geo-layers";
import { MVTLoader } from "@loaders.gl/mvt";
import { PMTiles } from "pmtiles";

/**
 * MultiPolygons out, Polygons only.
 *
 * The layers read `feature.geometry.coordinates` as one polygon — an array of
 * rings. A MultiPolygon's coordinates are an array of *polygons*, one level
 * deeper, and handing that to SolidPolygonLayer does not draw a wrong shape:
 * projectFlat meets a ring where it expects a point, an assertion throws
 * inside tessellation, and deck discards the whole sublayer's geometry. One
 * MultiPolygon in a tile of 141 buildings took all 141 down with it — rows
 * present, colours right, nothing on the GPU, for every tile that contained
 * at least one.
 *
 * The whole-file path has always split these (toPolygons, one row per part).
 * This is the same normalisation at the tile parse, so nothing downstream
 * ever sees the deeper nesting.
 */
export function splitMultiPolygons(features) {
  if (!Array.isArray(features)) return features;
  let out = null;
  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i];
    if (feature?.geometry?.type !== "MultiPolygon") {
      if (out) out.push(feature);
      continue;
    }
    // Copy-on-first-hit: most tiles have no MultiPolygons at all, and they
    // should not pay for an array rebuild.
    if (!out) out = features.slice(0, i);
    for (const polygon of feature.geometry.coordinates) {
      out.push({
        ...feature,
        geometry: { type: "Polygon", coordinates: polygon },
      });
    }
  }
  return out ?? features;
}

/** One archive per URL. Opening it re-reads the header and directory. */
const archives = new Map();

export function openArchive(url) {
  let archive = archives.get(url);
  if (!archive) {
    archive = new PMTiles(url);
    archives.set(url, archive);
  }
  return archive;
}

/**
 * A TileLayer backed by a PMTiles archive.
 *
 * `renderSubLayers` receives the parsed features for one tile and returns
 * whatever should draw them, so callers keep full control of styling — this
 * module only handles getting the geometry out of the file.
 */
export function pmTileLayer({
  id,
  url,
  minZoom = 0,
  maxZoom = 16,
  renderSubLayers,
  visible = true,
  updateTriggers,
  ...rest
}) {
  const archive = openArchive(url);

  return new TileLayer({
    id,
    visible,
    minZoom,
    maxZoom,
    // Past the archive's top zoom deck.gl reuses the deepest tile it has
    // rather than asking for one that was never written.
    extent: undefined,
    tileSize: 512,
    updateTriggers,

    async getTileData({ index, signal }) {
      const { x, y, z } = index;
      let tile;
      try {
        tile = await archive.getZxy(z, x, y);
      } catch {
        // A missing tile is ordinary: most of the world is not in this
        // archive, and asking for empty ocean should not raise.
        return null;
      }
      if (!tile?.data || signal?.aborted) return null;

      const features = await MVTLoader.parse(tile.data, {
        mvt: {
          // Required, and its absence throws rather than defaulting — every
          // tile was failing to parse, which is why buildings and greenery
          // disappeared the moment they moved to tiles.
          shape: "geojson",
          // World coordinates, so the returned features can be handed
          // straight to a layer that expects lng/lat like every other layer
          // here does.
          coordinates: "wgs84",
          tileIndex: { x, y, z },
        },
      });

      return splitMultiPolygons(features);
    },

    renderSubLayers,
    ...rest,
  });
}

/**
 * deck.gl hands `renderSubLayers` its data as a promise on the first call.
 *
 * Returning a layer built from an unresolved promise draws nothing and logs
 * nothing, which is a genuinely confusing way to see an empty map. Callers use
 * this to bail until the features are actually there.
 */
export function tileFeatures(props) {
  const data = props.data;
  return Array.isArray(data) ? data : null;
}
